"use client";

import { c as _c } from "react/compiler-runtime";
import React from "react";
import { CompactMenu } from "./CompactMenu";
import clsx from "clsx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown } from "@fortawesome/free-solid-svg-icons";
import { TAB_TOGGLE_WITH_OVERFLOW_MESSAGES } from "@/i18n/messages";
interface TabOption {
  readonly key: string;
  readonly label: string;
}
interface TabToggleWithOverflowProps {
  readonly options: readonly TabOption[];
  readonly activeKey: string;
  readonly onSelect: (key: string) => void;
  readonly maxVisibleTabs?: number | undefined;
  readonly fullWidth?: boolean | undefined;
}
interface OverflowTriggerProps {
  readonly isOpen?: boolean | undefined;
  readonly isActiveInOverflow: boolean;
  readonly activeLabel?: string | undefined;
  readonly fallbackLabel: string;
}
const OverflowTrigger: React.FC<OverflowTriggerProps> = t0 => {
  const $ = _c(8);
  const {
    isOpen: t1,
    isActiveInOverflow,
    activeLabel,
    fallbackLabel
  } = t0;
  const isOpen = t1 === undefined ? false : t1;
  const t2 = isActiveInOverflow ? activeLabel ?? fallbackLabel : fallbackLabel;
  const t3 = isOpen ? "tw-rotate-180" : "";
  let t4;
  if ($[0] !== t3) {
    t4 = clsx("tw-ml-0.5 tw-inline-flex tw-transition-transform tw-duration-200", t3);
    $[0] = t3;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  let t5;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <FontAwesomeIcon icon={faChevronDown} aria-hidden="true" className="tw-h-3 tw-w-3 tw-opacity-70" />;
    $[2] = t5;
  } else {
    t5 = $[2];
  }
  let t6;
  if ($[3] !== t4) {
    t6 = <span className={t4}>{t5}</span>;
    $[3] = t4;
    $[4] = t6;
  } else {
    t6 = $[4];
  }
  let t7;
  if ($[5] !== t2 || $[6] !== t6) {
    t7 = <>{t2}{t6}</>;
    $[5] = t2;
    $[6] = t6;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  return t7;
};
export const TabToggleWithOverflow: React.FC<TabToggleWithOverflowProps> = t0 => {
  const $ = _c(59);
  const {
    options,
    activeKey,
    onSelect,
    maxVisibleTabs: t1,
    fullWidth: t2
  } = t0;
  const maxVisibleTabs = t1 === undefined ? 3 : t1;
  const fullWidth = t2 === undefined ? false : t2;
  const clampedMax = Math.max(0, Math.floor(maxVisibleTabs));
  let t3;
  if ($[0] !== clampedMax || $[1] !== options) {
    t3 = options.slice(0, clampedMax);
    $[0] = clampedMax;
    $[1] = options;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const v = t3;
  let t4;
  if ($[3] !== clampedMax || $[4] !== options) {
    t4 = options.length > clampedMax ? options.slice(clampedMax) : [];
    $[3] = clampedMax;
    $[4] = options;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const o = t4;
  let t5;
  if ($[6] !== o || $[7] !== v) {
    t5 = [v, o];
    $[6] = o;
    $[7] = v;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const [visibleTabs, overflowTabs] = t5 as const;
  let t6;
  if ($[9] !== activeKey || $[10] !== options) {
    let t7;
    if ($[12] !== activeKey) {
      t7 = option => option.key === activeKey;
      $[12] = activeKey;
      $[13] = t7;
    } else {
      t7 = $[13];
    }
    t6 = options.find(t7);
    $[9] = activeKey;
    $[10] = options;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const activeOption = t6;
  let t7;
  if ($[14] !== activeKey || $[15] !== overflowTabs) {
    let t8;
    if ($[17] !== activeKey) {
      t8 = tab => tab.key === activeKey;
      $[17] = activeKey;
      $[18] = t8;
    } else {
      t8 = $[18];
    }
    t7 = overflowTabs.some(t8);
    $[14] = activeKey;
    $[15] = overflowTabs;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  const isActiveInOverflow = t7;
  let t8;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = [];
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  const tabRefs = React.useRef(t8);
  let t9;
  if ($[20] !== activeKey) {
    t9 = tab_0 => tab_0.key === activeKey;
    $[20] = activeKey;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  const activeVisibleIndex = visibleTabs.findIndex(t9);
  let t10;
  if ($[22] !== activeVisibleIndex) {
    t10 = () => Math.max(activeVisibleIndex, 0);
    $[22] = activeVisibleIndex;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  const [focusedTabIndex, setFocusedTabIndex] = React.useState(t10);
  let t11;
  if ($[24] !== onSelect) {
    t11 = key => {
      onSelect(key);
    };
    $[24] = onSelect;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  const handleSelect = t11;
  let t12;
  let t13;
  if ($[26] !== visibleTabs.length) {
    t12 = () => {
      tabRefs.current = tabRefs.current.slice(0, visibleTabs.length);
    };
    t13 = [visibleTabs.length];
    $[26] = visibleTabs.length;
    $[27] = t12;
    $[28] = t13;
  } else {
    t12 = $[27];
    t13 = $[28];
  }
  React.useEffect(t12, t13);
  let t14;
  let t15;
  if ($[29] !== activeVisibleIndex || $[30] !== visibleTabs.length) {
    t14 = () => {
      if (activeVisibleIndex >= 0) {
        setFocusedTabIndex(activeVisibleIndex);
        tabRefs.current[activeVisibleIndex]?.focus();
        return;
      }
      setFocusedTabIndex(currentIndex => {
        if (visibleTabs.length === 0) {
          return currentIndex;
        }
        const lastIndex = visibleTabs.length - 1;
        return Math.min(currentIndex, lastIndex);
      });
    };
    t15 = [activeVisibleIndex, visibleTabs.length];
    $[29] = activeVisibleIndex;
    $[30] = visibleTabs.length;
    $[31] = t14;
    $[32] = t15;
  } else {
    t14 = $[31];
    t15 = $[32];
  }
  React.useEffect(t14, t15);
  let t16;
  if ($[33] !== visibleTabs.length) {
    t16 = (event, currentIndex_0) => {
      if (visibleTabs.length <= 1) {
        return;
      }
      const {
        key: key_0
      } = event;
      if (!["ArrowRight", "ArrowLeft", "Home", "End"].includes(key_0)) {
        return;
      }
      event.preventDefault();
      const lastIndex_0 = visibleTabs.length - 1;
      let nextIndex = currentIndex_0;
      if (key_0 === "ArrowRight") {
        nextIndex = currentIndex_0 === lastIndex_0 ? 0 : currentIndex_0 + 1;
      } else {
        if (key_0 === "ArrowLeft") {
          nextIndex = currentIndex_0 === 0 ? lastIndex_0 : currentIndex_0 - 1;
        } else {
          if (key_0 === "Home") {
            nextIndex = 0;
          } else {
            if (key_0 === "End") {
              nextIndex = lastIndex_0;
            }
          }
        }
      }
      setFocusedTabIndex(nextIndex);
      tabRefs.current[nextIndex]?.focus();
    };
    $[33] = visibleTabs.length;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  const handleVisibleTabKeyDown = t16;
  const t17 = fullWidth ? "tw-w-full" : "tw-w-auto";
  let t18;
  if ($[35] !== t17) {
    t18 = clsx("tw-flex tw-gap-x-1", t17);
    $[35] = t17;
    $[36] = t18;
  } else {
    t18 = $[36];
  }
  const t19 = fullWidth && "tw-flex-1";
  let t20;
  if ($[37] !== t19) {
    t20 = clsx("tw-flex tw-gap-x-1", t19);
    $[37] = t19;
    $[38] = t20;
  } else {
    t20 = $[38];
  }
  let t21;
  if ($[39] !== activeKey || $[40] !== focusedTabIndex || $[41] !== fullWidth || $[42] !== handleSelect || $[43] !== handleVisibleTabKeyDown || $[44] !== visibleTabs) {
    t21 = visibleTabs.map((option_0, index) => <button role="tab" aria-selected={activeKey === option_0.key} key={option_0.key} type="button" tabIndex={index === focusedTabIndex ? 0 : -1} onClick={() => handleSelect(option_0.key)} onKeyDown={event_0 => handleVisibleTabKeyDown(event_0, index)} ref={element => {
      tabRefs.current[index] = element;
    }} className={`tw-flex-1 tw-py-3 tw-whitespace-nowrap tw-text-sm tw-font-medium tw-border-b-2 tw-border-t-0 tw-border-x-0 tw-border-solid tw-bg-transparent tw-transition-all tw-duration-200 ${fullWidth ? "tw-text-center tw-justify-center tw-flex" : ""} ${activeKey === option_0.key ? "tw-text-white tw-border-primary-300" : "tw-text-iron-400 hover:tw-text-iron-200 tw-border-transparent"}`}>{option_0.label}</button>);
    $[39] = activeKey;
    $[40] = focusedTabIndex;
    $[41] = fullWidth;
    $[42] = handleSelect;
    $[43] = handleVisibleTabKeyDown;
    $[44] = visibleTabs;
    $[45] = t21;
  } else {
    t21 = $[45];
  }
  let t22;
  if ($[46] !== t20 || $[47] !== t21) {
    t22 = <div role="tablist" aria-orientation="horizontal" className={t20}>{t21}</div>;
    $[46] = t20;
    $[47] = t21;
    $[48] = t22;
  } else {
    t22 = $[48];
  }
  let t23;
  if ($[49] !== activeKey || $[50] !== activeOption?.label || $[51] !== handleSelect || $[52] !== isActiveInOverflow || $[53] !== overflowTabs) {
    t23 = overflowTabs.length > 0 && <CompactMenu className="tw-relative" unstyledTrigger={true} unstyledMenu={true} unstyledItems={true} triggerClassName={clsx("tw-flex-1 tw-py-3 tw-whitespace-nowrap tw-text-sm tw-font-medium tw-border-b-2 tw-border-t-0 tw-border-x-0 tw-border-solid tw-bg-transparent tw-transition-all tw-duration-200", isActiveInOverflow ? "tw-text-primary-300 tw-border-b-2 tw-border-primary-400" : "tw-text-iron-400 hover:tw-text-iron-200")} trigger={<OverflowTrigger isActiveInOverflow={isActiveInOverflow} activeLabel={activeOption?.label} fallbackLabel={TAB_TOGGLE_WITH_OVERFLOW_MESSAGES.overflowFallbackLabel} />} items={overflowTabs.map(option_1 => ({
      id: option_1.key,
      label: option_1.label,
      onSelect: () => handleSelect(option_1.key),
      active: activeKey === option_1.key
    }))} itemClassName="tw-block tw-w-full tw-border-0 tw-bg-transparent tw-px-4 tw-py-2 tw-text-left tw-text-sm tw-font-medium tw-transition-colors" inactiveItemClassName="tw-text-iron-300 hover:tw-bg-iron-800 hover:tw-text-iron-200" activeItemClassName="tw-text-primary-300" focusItemClassName="tw-bg-iron-800 tw-text-iron-100" menuWidthClassName="tw-w-36" menuClassName="tw-z-50 tw-mt-2 tw-rounded-md tw-bg-iron-900 tw-py-1 tw-shadow-lg tw-ring-1 tw-ring-primary-400/20 focus:tw-outline-none" itemsWrapperClassName="tw-py-1" anchor="bottom end" activeItemId={isActiveInOverflow ? activeKey : undefined} closeOnSelect={true} aria-label={TAB_TOGGLE_WITH_OVERFLOW_MESSAGES.overflowMenuAriaLabel} />;
    $[49] = activeKey;
    $[50] = activeOption?.label;
    $[51] = handleSelect;
    $[52] = isActiveInOverflow;
    $[53] = overflowTabs;
    $[54] = t23;
  } else {
    t23 = $[54];
  }
  let t24;
  if ($[55] !== t18 || $[56] !== t22 || $[57] !== t23) {
    t24 = <div className={t18}>{t22}{t23}</div>;
    $[55] = t18;
    $[56] = t22;
    $[57] = t23;
    $[58] = t24;
  } else {
    t24 = $[58];
  }
  return t24;
};